import { batch } from 'react-redux';
import { CAMERA_PRESENTER } from '@shoflo/polymer';
import {
  setDesiredAttendeeCameraStatus,
  setDesiredAttendeeMuteStatus,
  setProgram,
  setSoloLive,
  updateSource
} from 'actions/Broadcast';
import {
  getSourceValue,
  isCameraPresenter,
  isScreenPresenter,
  isSourceInProgram,
  muteSourcesRemovedFromProgram
} from 'utils/broadcastSourceHelper';
import { getBroadcastSourcesFromLayers } from '../utils';
import { toggleProgramSource } from '@shoflo/studio-redux/actions';
import { sendToDatadog } from '@studio/logger';
import { hasMeetingExceededCameraLimit } from '@shoflo/studio-redux/utils';

/**
 * Handles series of actions to be dispatched before taking a presenter source live (Add/Solo)
 */
function takePresenterLive(source, sourceActionConfig = {}) {
  return (dispatch, getState) => {
    const {
      attendeeCameraStatus,
      program: { layers = [] },
      broadcastSettings: { autoMutePresenters },
      broadcastSources,
      meeting: { meetingResponse, attendeeTiles }
    } = getState();

    const { isSoloLive } = sourceActionConfig;
    const { hidden: isSourceHidden, _id: sourceId } = source;
    const isLiveSource = isSourceInProgram(layers, sourceId);

    const sourceAction = isSoloLive ? setSoloLive : toggleProgramSource;
    const isScreenShare = isScreenPresenter(source);
    const sourceValue = getSourceValue(source);

    const addingSource = !layers.find(layer => layer.source._id === sourceId);
    const sources = getBroadcastSourcesFromLayers(
      layers,
      broadcastSources?.sources
    );

    const isCameraAtLimit = addingSource
      ? hasMeetingExceededCameraLimit(attendeeTiles, attendeeCameraStatus)
      : false;

    const meetingId = meetingResponse?.Meeting?.MeetingId;

    const removedSources = isSoloLive
      ? sources?.filter(src => src._id !== sourceId)
      : !addingSource
      ? [source]
      : [];

    removedSources?.forEach(src => {
      if (isCameraPresenter(src)) {
        const logPayload = {
          'studio.actionName': isSoloLive
            ? 'Set Solo Live'
            : 'Toggle Program Source',
          'studio.attendeeTileStatus': 'OFF-STAGE',
          'studio.tileAttendeeId': src.value,
          title: `ProgramUpdate: Attendee ${src.value} in meeting ${meetingId} is removed from the stage`
        };

        sendToDatadog('program-update', logPayload, logPayload.title, false);
      }
    });

    if (!isLiveSource && addingSource && !isCameraAtLimit) {
      const logPayload = {
        'studio.actionName': isSoloLive
          ? 'Set Solo Live'
          : 'Toggle Program Source',
        'studio.attendeeTileStatus': 'ON-STAGE',
        'studio.tileAttendeeId': source.value,
        title: `ProgramUpdate: Attendee ${source.value} in meeting ${meetingId} is added to the stage`
      };

      sendToDatadog('program-update', logPayload, logPayload.title, false);
    }

    const nextMuteStatus = isLiveSource;

    let isAttendeeCameraOn = attendeeCameraStatus[sourceValue];

    if (typeof isAttendeeCameraOn !== 'boolean') {
      isAttendeeCameraOn = true;
    }
    // should only result in one combined re-render, not multiple
    batch(() => {
      // We only want to unmute a source when they are going live. if they are live already no need to unmute
      if (!isLiveSource && autoMutePresenters && !isCameraAtLimit) {
        dispatch(setDesiredAttendeeMuteStatus(sourceValue, nextMuteStatus));
      } else if (isLiveSource && !isSoloLive && autoMutePresenters) {
        // we are removing this source(s) from live so mute
        dispatch(setDesiredAttendeeMuteStatus(sourceValue, true));
      }

      if (
        !isCameraAtLimit &&
        !isAttendeeCameraOn &&
        !isScreenShare &&
        !isLiveSource &&
        !source.cameraUnavailable
      ) {
        dispatch(
          setDesiredAttendeeCameraStatus(sourceValue, !isAttendeeCameraOn)
        );
      }

      // Set solo live action already has an update source action
      if (isSourceHidden && !isSoloLive) {
        dispatch(updateSource(sourceId, { hidden: !isSourceHidden }));
      }

      // if we set a camera presenter solo, we want to mute the sources being removed from program.
      if (
        isSoloLive &&
        source.type === CAMERA_PRESENTER &&
        autoMutePresenters
      ) {
        muteSourcesRemovedFromProgram(layers, source, dispatch);
      }

      dispatch(sourceAction(source, setProgram));
    });
  };
}

export default takePresenterLive;
